import { apiService } from "./apiService";
import axios, { AxiosError } from "axios";
import { ApiError } from "./apiError";
import { ServiceResponse } from "./serviceResponse";
import { Fabricante } from "../dtos/Fabricante";
import { EnvasesFilterParams } from "../dtos/EnvasesFilterParams";
import {
  AsignarProductoEnvaseDto,
  CreaEnvaseDto,
  Envase,
} from "../dtos/Envase";
import { TipoEnvase } from "../dtos/TipoEnvase";
import { CatCuerpo } from "../dtos/CatCuerpo";
import { CatCapuchones } from "../dtos/CatCapuchones";
import { CatCuello } from "../dtos/CatCuello";
import { CatEstarcido } from "../dtos/CatEstarcido";
import { CatOjiva } from "../dtos/CatOjiva";
import { Valvula } from "../dtos/Valvula";
import { Modelo } from "../dtos/Modelo";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const getFabricantes = async (): Promise<ServiceResponse<Fabricante[]>> => {
  try {
    const data = await apiService.get<Fabricante[]>("/api/Catalogos/Fabricantes");
    return { data, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

const getModelos = async (): Promise<ServiceResponse<Modelo[]>> => {
  try {
    const data = await apiService.get<Modelo[]>("/api/Catalogos/Modelos");
    return { data, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

export interface CatalogosEnvase {
  tiposEnvase: TipoEnvase[];
  cuerpos: CatCuerpo[];
  capuchones: CatCapuchones[];
  cuellos: CatCuello[];
  estarcidos: CatEstarcido[];
  ojivas: CatOjiva[];
  valvulas: Valvula[];
  fabricantes: Fabricante[];
  modelos: Modelo[];
}

const loadCatalogosEnvase = async (): Promise<CatalogosEnvase> => {
  const [
    tiposEnvase,
    cuerpos,
    capuchones,
    cuellos,
    estarcidos,
    ojivas,
    valvulas,
    fabricantes,
    modelos,
  ] = await Promise.all([
    apiService.get<TipoEnvase[]>("/api/Catalogos/TiposEnvase"),
    apiService.get<CatCuerpo[]>("/api/Catalogos/CatCuerpos"),
    apiService.get<CatCapuchones[]>("/api/Catalogos/CatCapuchones"),
    apiService.get<CatCuello[]>("/api/Catalogos/CatCuellos"),
    apiService.get<CatEstarcido[]>("/api/Catalogos/CatEstarcidos"),
    apiService.get<CatOjiva[]>("/api/Catalogos/CatOjivas"),
    apiService.get<Valvula[]>("/api/Catalogos/Valvulas"),
    apiService.get<Fabricante[]>("/api/Catalogos/Fabricantes"),
    apiService.get<Modelo[]>("/api/Catalogos/Modelos"),
  ]);

  return {
    tiposEnvase,
    cuerpos,
    capuchones,
    cuellos,
    estarcidos,
    ojivas,
    valvulas,
    fabricantes,
    modelos,
  };
};

const buildQS = (p: EnvasesFilterParams) => {
  const qs = new URLSearchParams();
  if (p.codigo) qs.append("codigo", p.codigo);
  if (p.nserie) qs.append("nserie", p.nserie);
  if (p.fabricanteId) qs.append("fabricanteId", p.fabricanteId);
  if (p.modeloId) qs.append("modeloId", p.modeloId);
  if (p.from) qs.append("from", p.from);
  if (p.to) qs.append("to", p.to);
  return qs.toString();
};

export const envasesService = {
  async getEnvasesFiltered(params: EnvasesFilterParams): Promise<Envase[]> {
    const qs = buildQS(params);
    const url = `/api/Envases/EnvasesFiltered${qs ? `?${qs}` : ""}`;
    return apiService.getRaw<Envase[]>(url);
  },

  async createEnvase(payload: CreaEnvaseDto): Promise<unknown> {
    return apiService.post("/api/Envases", payload);
  },

  async getEnvasesSinProducto(): Promise<Envase[]> {
    return apiService.getRaw<Envase[]>("/api/Envases/sinProducto");
  },

  async asignarProducto(
    id: string,
    payload: AsignarProductoEnvaseDto,
  ): Promise<unknown> {
    return apiService.put("/api/Envases/" + id + "/producto", payload);
  },

  getFabricantes,
  getModelos,
  loadCatalogosEnvase,
  buildQS,
};
